import type { SupabaseClient } from '@supabase/supabase-js';

export type GalleryImageCore = {
    id: string;
    contact_id: string;
    url: string;
    source_type: 'manual' | 'event' | 'relationship' | 'todo';
    source_id?: string | null;
    tags: string[];
    created_at?: string;
};

export async function listContactImagesCore(sb: SupabaseClient, contactId: string): Promise<GalleryImageCore[]> {
    const { data, error } = await sb
        .from('contact_images')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as GalleryImageCore[];
}

export async function addContactImageCore(
    sb: SupabaseClient,
    contactId: string,
    url: string,
    opts?: { source_type?: 'manual' | 'event' | 'relationship' | 'todo'; source_id?: string | null; tags?: string[] }
) {
    const payload = {
        contact_id: contactId,
        url,
        source_type: opts?.source_type || 'manual',
        source_id: opts?.source_id || null,
        tags: opts?.tags || [],
    };
    const { data, error } = await sb
        .from('contact_images')
        .insert(payload)
        .select('*')
        .single();
    if (error) throw error;
    return data as GalleryImageCore;
}

export async function deleteContactImageCore(sb: SupabaseClient, id: string) {
    const { error } = await sb.from('contact_images').delete().eq('id', id);
    if (error) throw error;
}

export async function updateContactImageCore(sb: SupabaseClient, id: string, patch: Partial<Pick<GalleryImageCore, 'tags' | 'url'>>) {
    const { data, error } = await sb
        .from('contact_images')
        .update({ ...patch })
        .eq('id', id)
        .select('*')
        .single();
    if (error) throw error;
    return data as GalleryImageCore;
}

export async function setDefaultProfileImageCore(sb: SupabaseClient, contactId: string, url: string) {
    const { error } = await sb
        .from('contacts')
        .update({ profile_image_url: url, updated_at: new Date().toISOString() })
        .eq('id', contactId);
    if (error) throw error;
}

export async function uploadContactImageCore(sb: SupabaseClient, file: File, contactId: string): Promise<string> {
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const name = `${contactId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error } = await sb.storage.from('images').upload(name, file, { upsert: false });
    if (error) throw error;
    const { data } = sb.storage.from('images').getPublicUrl(name);
    return data.publicUrl as string;
}
